import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import moment, { Moment } from 'moment';
import { EditUserEmailGQL, EditUsernameGQL, SetUserPremiumGQL, UserDetailsFragment } from '@/core/graphql/graphql';
import { BACKEND_DATETIME_FORMAT, EMAIL_REG } from "@/utils/constants";
import { DateTime } from "luxon";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { defaultIfEmpty } from "rxjs/operators";
import { forkJoin, Observable } from "rxjs";

@Component({
  selector: 'cosmos-edit-user',
  templateUrl: './edit-user-modal.component.html',
  styleUrls: ['./edit-user-modal.component.scss'],
})
export class EditUserModalComponent {
  public readonly _editUserForm: FormGroup;
  private readonly _dateTime = new FormControl(null);
  private readonly _username = new FormControl(null, Validators.required);
  private readonly _email = new FormControl(null, [Validators.required, Validators.pattern(EMAIL_REG)]);
  private readonly _startingState: { date: Moment | null, username: string, email: string };

  public constructor(
    private readonly _modalRef: MatDialogRef<EditUserModalComponent>,
    private readonly _formBuilder: FormBuilder,
    private readonly _editUsernameGql: EditUsernameGQL,
    private readonly _setUserPremiumGql: SetUserPremiumGQL,
    private readonly _editUserEmail: EditUserEmailGQL,
    @Inject(MAT_DIALOG_DATA) private readonly _data: { user: UserDetailsFragment },
  ) {
    const date = this._data.user.subscription?.subscriptionValidTo;
    const momentDate = date ? moment.utc(date) : date;

    this._startingState = { username: this._data.user.username!, date : momentDate, email: this._data.user.email };
    this._username.setValue(this._startingState.username, { emitEvent: false });
    this._dateTime.setValue(this._startingState.date, { emitEvent: false });
    this._email.setValue(this._startingState.email, { emitEvent: false });

    this._editUserForm = new FormGroup({
      username: this._username,
      dateTime: this._dateTime,
      email: this._email,
    });
  }

  public submitForm(): void {
    const observableArray: Observable<any>[] = [];

    if (this._dateTime.value !== this._startingState.date) {
      observableArray.push(this._setUserPremiumGql
        .mutate({
          userId: this._data.user.id,
          expirationDate: DateTime.fromJSDate(this._dateTime.value.toDate()).toFormat(BACKEND_DATETIME_FORMAT),
        }));
    }

    if (this._username.value !== this._startingState.username) {
      observableArray.push(this._editUsernameGql
        .mutate({
          userId: this._data.user.id,
          username: this._username.value,
        }));
    }

    if (this._email.value !== this._startingState.email) {
      observableArray.push(this._editUserEmail
        .mutate({
          userId: this._data.user.id,
          name: this._data.user.name!,
          email: this._email.value,
        }));
    }

    forkJoin(...observableArray).pipe(
      defaultIfEmpty(null),
    ).subscribe(_ => this._modalRef.close(true));
  }

  public discardForm(): void {
    this._modalRef.close();
  }
}
